/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  CustomerType,
  DealType,
  IConfiguration,
  ILocation,
  Interest,
  LocationType,
  Maybe,
  StorageTime
} from 'src/types/graphql'
import { safeStorageGet, safeStorageSet } from 'src/utils/safeStorage'
import { recordBrowserLeadCapi } from './capi'
import { canonicalCity, norm } from './common'
import {
  AnalyticsDealType,
  buildEnrichment,
  DealTypeSlug,
  EnrichedEventParams,
  getAnalyticsDealType,
  getProductType,
  getTotalDealValue,
  getVertical,
  ProductType,
  Vertical
} from './enrichment'
import fbp from './fbp'
import ga, { Gtag } from './gtag'
import { isInternalTraffic } from './internal'
import linkedin from './linkedin'
import oaiq from './oaiq'
import { buildOnDemandCardViewKey } from './onDemandCardViewKey'
import posthog from './posthog'
import ms from './uetq'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getEventId = (): string =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

export const pushToDataLayer = (event: string, params: Record<string, any>) => {
  if (typeof window === 'undefined') return
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ event, ...params })
}

/** Shared geo fields extracted from a location's addressDetails. */
const geoFields = (location: ILocation | undefined) => {
  const ad = location?.addressDetails
  return {
    city: norm(ad?.city),
    state: norm(ad?.state),
    hood: norm(ad?.neighborhood ?? ad?.colonia),
    // `zone` is the canonical city SLUG, deliberately alongside `city` (the
    // normalized display name) rather than replacing it. It is the join key
    // for the directory dilution guardrail (plan §4.2): the search-side CTR
    // events emit the same slug, so verified-listing CTR and checkout-start
    // rate can be sliced on ONE dimension. `city` stays exactly as it was —
    // every existing marketing query reads it.
    zone: canonicalCity(ad?.city)
  }
}

// ---------------------------------------------------------------------------
// Segmented conversion events
// ---------------------------------------------------------------------------

const PRODUCT_CONTEXT_KEY = 'spotme_product_context'
const LAST_SEEN_LOCATION_KEY = 'spotme_last_seen_location'

export type ProductContext = {
  vertical: Vertical
  product_type: ProductType
  deal_type?: AnalyticsDealType
  locationType?: LocationType
  locationId?: string
  configurationId?: string
  price?: number
  quantity?: number
  storageTime?: StorageTime | ''
}

/** Safe localStorage getter — returns null on any failure (SSR, disabled, etc.) */
const safeGetItem = (key: string): string | null => {
  try {
    return typeof window !== 'undefined' ? localStorage.getItem(key) : null
  } catch {
    return null
  }
}

/** Safe localStorage setter — silently fails on any error. */
const safeSetItem = (key: string, value: string): void => {
  try {
    if (typeof window !== 'undefined') localStorage.setItem(key, value)
  } catch {
    // localStorage unavailable (SSR, private browsing, quota exceeded)
  }
}

/** Safe localStorage remove — silently fails on any error. */
const safeRemoveItem = (key: string): void => {
  try {
    if (typeof window !== 'undefined') localStorage.removeItem(key)
  } catch {
    // empty catch block
  }
}

/**
 * Persist product context so completeKyc can read it later.
 * Also saves a "last seen location" copy that never gets consumed —
 * used as a fallback when a user enters KYC without listing context
 * (e.g. from homepage or direct link).
 */
const saveProductContext = (ctx: ProductContext) => {
  safeSetItem(PRODUCT_CONTEXT_KEY, JSON.stringify(ctx))
  safeSetItem(LAST_SEEN_LOCATION_KEY, JSON.stringify(ctx))
}

/** Read and clear the product context saved during the browsing session. */
const consumeProductContext = (): ProductContext | null => {
  const raw = safeGetItem(PRODUCT_CONTEXT_KEY)
  if (!raw) return null
  safeRemoveItem(PRODUCT_CONTEXT_KEY)
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

/**
 * Read the product context WITHOUT clearing it. `completeKyc` consumes the
 * context at the END of the KYC funnel; mid-funnel readers (`kyc_step`) must
 * peek, or the terminal `complete_kyc` would lose its listing enrichment.
 */
const peekProductContext = (): ProductContext | null => {
  const raw = safeGetItem(PRODUCT_CONTEXT_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

/**
 * Read the last-seen location context (never cleared).
 * Used as a fallback when no active product context exists — e.g. a user
 * who browsed a listing days ago then came back to sign up directly.
 */
export const getLastSeenLocation = (): ProductContext | null => {
  const raw = safeGetItem(LAST_SEEN_LOCATION_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

/**
 * Resolve which segmented event(s) to fire based on vertical/product_type.
 * A single conversion can fire multiple segmented events — e.g. a parking
 * fleet deal (qty > 5) fires BOTH parking_monthly_* AND parking_b2b_*.
 *
 * B2B fleet parking fires full-funnel events (purchase, complete_kyc,
 * qualified_lead) because fleet renters can convert both through sales-led
 * consultative flows AND self-serve on-platform checkout.
 */
const PRO_MIN_PARKING_UNITS = 3

const isB2bParking = (
  productType: ProductType,
  vertical: Vertical,
  quantity?: number
): boolean =>
  productType === 'parking_b2b' ||
  (vertical === 'parking' && !!quantity && quantity >= PRO_MIN_PARKING_UNITS)

const getSegmentedEvents = (
  baseEvent: 'purchase' | 'complete_kyc',
  vertical: Vertical,
  productType: ProductType,
  quantity?: number
): Gtag[] => {
  const events: Gtag[] = []
  if (vertical === 'storage') {
    events.push(
      baseEvent === 'purchase' ? 'storage_purchase' : 'storage_complete_kyc'
    )
  } else if (vertical === 'parking') {
    // All parking gets the monthly event
    events.push(
      baseEvent === 'purchase'
        ? 'parking_monthly_purchase'
        : 'parking_monthly_complete_kyc'
    )
    // Fleet / B2B parking fires full-funnel B2B events
    if (isB2bParking(productType, vertical, quantity)) {
      events.push(
        baseEvent === 'purchase'
          ? 'parking_b2b_purchase'
          : 'parking_b2b_complete_kyc'
      )
      events.push('parking_b2b_qualified_lead')
    }
  }
  return events
}

/**
 * Resolve segmented event names for an add_to_cart sub-event.
 *
 * Layering:
 *   1. Vertical-only:        `${subEvent}_${vertical}`           (e.g. contact_wa_storage)
 *   2. Vertical + dealtype:  `${subEvent}_${vertical}_${slug}`   (e.g. contact_wa_storage_traditional)
 *
 * Layer 2 is skipped when the sub-event only ever fires on a single dealtype
 * (begin_checkout / chat_host = always SpotMe Now, broker_contact = always
 * Broker, traditional_contact = always Traditional). In those cases the
 * dealtype is implicit in the sub-event name itself and the suffix would be
 * redundant noise in GA4.
 *
 * `traditional_contact` now fires on BOTH storage and parking listings
 * (parking operators are an off-platform Traditional lead-gen tier too), so
 * it has a storage and a parking vertical-only variant.
 *
 * Only emits names declared in the `Gtag` union — unsupported combinations
 * silently produce no segmented events.
 */
export const ATC_VERTICAL_NAMES: Record<
  AddToCartSubEvent,
  Partial<Record<Vertical, Gtag>>
> = {
  contact_wa: {
    storage: 'contact_wa_storage',
    parking: 'contact_wa_parking'
  },
  begin_checkout: {
    storage: 'begin_checkout_storage',
    parking: 'begin_checkout_parking'
  },
  broker_contact: {
    storage: 'broker_contact_storage',
    parking: 'broker_contact_parking'
  },
  traditional_contact: {
    storage: 'traditional_contact_storage',
    parking: 'traditional_contact_parking'
  },
  chat_host: {
    storage: 'chat_host_storage',
    parking: 'chat_host_parking'
  },
  technical_report: {},
  // on_demand fires from the Almacenamiento a Domicilio lead form. The lead
  // routes through the Traditional funnel (specialist hand-off, no on-platform
  // checkout) so we don't have a dedicated segmented event for it yet — the
  // bare sub-event + enrichment carry the signal. Add an entry here to satisfy
  // the Record<AddToCartSubEvent, ...> shape; segmented event names can be
  // added later once campaigns ask for them.
  on_demand: {}
}

const ATC_DEALTYPE_NAMES: Partial<
  Record<
    AddToCartSubEvent,
    Partial<Record<Vertical, Partial<Record<DealTypeSlug, Gtag>>>>
  >
> = {
  contact_wa: {
    storage: {
      now: 'contact_wa_storage_now',
      broker: 'contact_wa_storage_broker',
      traditional: 'contact_wa_storage_traditional'
    },
    parking: {
      now: 'contact_wa_parking_now',
      broker: 'contact_wa_parking_broker'
    }
  }
  // begin_checkout / broker_contact / traditional_contact / chat_host
  // are single-dealtype and don't get a dealtype suffix.
}

const getSegmentedAtcEvents = (
  subEvent: AddToCartSubEvent,
  vertical: Vertical,
  dealTypeSlug: DealTypeSlug
): Gtag[] => {
  const events: Gtag[] = []
  const verticalName = ATC_VERTICAL_NAMES[subEvent]?.[vertical]
  if (verticalName) events.push(verticalName)
  const dealTypeName = ATC_DEALTYPE_NAMES[subEvent]?.[vertical]?.[dealTypeSlug]
  if (dealTypeName) events.push(dealTypeName)
  return events
}

/**
 * Tier rollups — every primary/secondary tier event also fires a single
 * `{vertical}_{tier}` rollup so Google Ads campaign goals can optimize on
 * 4 events instead of importing 10+ individual GA4 Key Events per campaign.
 *
 * Strategy + tier mapping is the source-of-truth for the per-pillar campaign
 * primary/secondary mix — see `docs/marketing/analytics-reference.md`.
 *
 * The WhatsApp closed-loop (`qualified_whatsapp_lead_storage` / `_parking`)
 * is NOT rolled up here because it fires SERVER-SIDE from
 * `spotme-server/src/LangGraphAgent/agent.ts` via Google Ads offline
 * conversion upload (different mechanism — Upload-API action type, not
 * GA4-imported). The user adds the WA action ID alongside the rollup action
 * as a Primary on the Storage/Parking Google Ads campaign goal.
 *
 * The rollup ONLY fires to GA4 + GTM dataLayer. Meta / Bing / LinkedIn keep
 * their existing funnel-specific custom events — those platforms either
 * have their own consolidation (Meta `Lead` standard event), don't need it
 * (Bing per-goal config), or use action-style conversions (LinkedIn) where
 * a rollup would conflict with funnel-specific optimization.
 */
export const TIER_ROLLUP_MAP: Partial<Record<Gtag, Gtag>> = {
  // ---- Storage PRIMARIES (Monthly Now + Traditional + OnDemand only — NO broker) ----
  // Storage tier intentionally EXCLUDES broker-model bodegas comerciales /
  // naves industriales (`broker_lead_storage`, `broker_contact_storage`,
  // `contact_wa_storage_broker`). Broker is an off-platform sales flow with
  // different unit economics and a different audience — including it pollutes
  // the storage campaign goal with mixed-intent signal.
  //
  // Defense-in-depth: even if a broker listing has `dealType` unset AND price
  // ≤ $30k (sub-threshold), `buildEnrichment` calls `getEffectiveDealType`
  // with the full config — which applies the warehouse/industrial pillar
  // promotion (sqm > 40, non-parking) → `deal_type='broker'` → slug
  // `'broker'` → segmented events land on `contact_wa_storage_broker` /
  // `broker_contact_storage` (not in this map) instead of
  // `contact_wa_storage_now` (would roll up into storage_secondary).
  // Without that rule, sub-$30k bodega listings polluted `storage_secondary`
  // and re-trained PMax storage bidding on broker inventory.
  // Bare `traditional_lead` is intentionally NOT rolled up (it's
  // vertical-agnostic — mixing storage + parking Traditional would re-train
  // the wrong campaign). Storage coverage now flows through the
  // vertical-scoped `traditional_lead_storage`; parking through
  // `traditional_lead_parking` (parking PRIMARIES below). Mirrors broker_lead.
  traditional_lead_storage: 'storage_primary',
  storage_purchase: 'storage_primary',
  storage_on_demand_lead: 'storage_primary',
  // /reservar phone-submit on a self-storage Monthly listing. Real HubSpot
  // lead created at this moment (parallel to qualified_whatsapp_lead_storage,
  // which is also storage_primary). Upstream of `storage_purchase` — a user
  // who completes checkout will fire both, which IS the intended Smart
  // Bidding signal (more volume for learning, naturally lower-weighted by
  // `getMarketingValue` since preauth carries no rent value).
  generate_lead_preauth_storage: 'storage_primary',
  // ---- Storage SECONDARIES (Monthly Now + Traditional + OnDemand only — NO broker) ----
  traditional_contact_storage: 'storage_secondary',
  begin_checkout_storage: 'storage_secondary',
  // /reservar page mount on a self-storage listing — parallel to
  // `begin_checkout_storage` for the unauthed-Monthly cohort that goes
  // through /reservar instead of the legacy auth-modal flow.
  begin_checkout_preauth_storage: 'storage_secondary',
  storage_on_demand_whatsapp: 'storage_secondary',
  storage_complete_kyc: 'storage_secondary',
  contact_wa_storage_now: 'storage_secondary',
  contact_wa_storage_traditional: 'storage_secondary',
  // ---- Parking PRIMARIES ----
  // Parking tier INCLUDES the Flotillas fleet flow (`parking_b2b_*`,
  // `broker_lead_parking`) because fleet B2B parking IS a SpotMe-Now-like
  // signal (on-platform conversion intent on multi-unit parking listings) —
  // not an off-platform broker flow.
  parking_b2b_form_submit: 'parking_primary',
  parking_b2b_qualified_lead: 'parking_primary',
  parking_monthly_purchase: 'parking_primary',
  parking_b2b_purchase: 'parking_primary',
  broker_lead_parking: 'parking_primary',
  traditional_lead_parking: 'parking_primary',
  // /reservar phone-submit on a parking Monthly listing — sibling of
  // generate_lead_preauth_storage. Same Smart Bidding rationale.
  generate_lead_preauth_parking: 'parking_primary',
  // ---- Parking SECONDARIES ----
  begin_checkout_parking: 'parking_secondary',
  // /reservar page mount on a parking listing — sibling of
  // begin_checkout_preauth_storage.
  begin_checkout_preauth_parking: 'parking_secondary',
  contact_wa_parking_now: 'parking_secondary',
  contact_wa_parking_broker: 'parking_secondary',
  parking_b2b_complete_kyc: 'parking_secondary',
  parking_monthly_complete_kyc: 'parking_secondary',
  broker_contact_parking: 'parking_secondary',
  traditional_contact_parking: 'parking_secondary',
  // ---- Host (supply-side) PRIMARIES ----
  // `supply_lead` is the bare bucket — bound to `host_primary` by default,
  // BUT funnel-aware override in fireSupplyEvent demotes the `individual`
  // funnel to `host_secondary`. Reason: `individual` fires from "Subir tu
  // espacio" CTA clicks on /anfitrion (via useHandleSubmitLocation) and
  // captures NO contact info — just a button-tap intent. The 5 form-submit
  // funnels (business, traditional, calculate, broker, invite) DO capture
  // name/email/phone and push to HubSpot via sendCustomerInfoEmail, so
  // those keep `host_primary`. `supply_listing_posted` is the deepest
  // signal — AddPostFlow Success step, i.e. an actual listing published.
  // Per-funnel `supply_lead_${funnel}` variants are NOT in the map (would
  // double-count — they fire alongside the bare event).
  supply_lead: 'host_primary',
  supply_listing_posted: 'host_primary',
  // ---- Host (supply-side) SECONDARIES (engagement) ----
  // `supply_view` (page mount) is intentionally NOT in the rollup — pure
  // pageview, too thin for Smart Bidding. Still fires for GA4 + PostHog
  // analytics, just not rolled up into `host_secondary`. `supply_lead_start`
  // (first form-field focus on a /anfitrion form) IS rolled up — form
  // engagement is a step above a pageview.
  supply_lead_start: 'host_secondary'
}

/** Fire a segmented event to all ad platforms. */
const fireSegmentedEvent = (eventName: Gtag, payload: Record<string, any>) => {
  pushToDataLayer(`spotme_${eventName}`, payload)
  ga.event(eventName, payload)
  fbp.trackCustom(eventName, payload)
  ms.event(eventName as any, {
    category: eventName,
    label: payload.event_id,
    value: payload.value?.toString()
  })
  linkedin.trackConversion(eventName)

  // Tier rollup — see TIER_ROLLUP_MAP above. Recursion-safe: rollup events
  // (storage_primary / storage_secondary / parking_primary / parking_secondary)
  // are not themselves in the map, so they fire exactly once.
  const rollup = TIER_ROLLUP_MAP[eventName]
  if (rollup) {
    pushToDataLayer(`spotme_${rollup}`, payload)
    ga.event(rollup, payload)

    // OpenAI / ChatGPT Ads — `storage_primary` ONLY for now (2026-08-21).
    // Scoped deliberately: the ChatGPT Ads campaign is a storage-only
    // risk-sleeve test, and its CONVERSIONS objective accepts only STANDARD
    // events, so our rollup maps onto the platform's "Lead Created".
    // Do NOT widen to parking/host without a campaign that optimizes on them
    // — an unused conversion just dilutes a brand-new bidder. See oaiq.ts.
    if (rollup === 'storage_primary') oaiq.leadCreated(payload.event_id)
  }
}

// ---------------------------------------------------------------------------
// 1. view_item
// ---------------------------------------------------------------------------

type ViewItemParams = {
  location: ILocation
  config: IConfiguration
  /** Display label from configLocMap */
  spaceType: string
}

export const viewItem = ({ location, config, spaceType }: ViewItemParams) => {
  if (isInternalTraffic()) return
  const eventId = getEventId()
  const { size, id, locationType, pillarPage } = config
  const price = config.price ?? 0
  const title = `${size?.length}x${size?.width} - ${spaceType}`
  const sqm = Math.round((size?.length ?? 0) * (size?.width ?? 0))
  const enrichment = buildEnrichment(config)
  const geo = geoFields(location)

  const payload = {
    event_id: eventId,
    // Flat config_id (the configuration UUID) so the PostHog mirror can slice
    // listing-page performance per listing and JOIN to the search-card events
    // (search_result_view / search_result_click also key on `config_id`). The
    // GA4 `items[].item_id` / Meta `content_ids[]` below carry the same id for
    // ecommerce, but those nested shapes don't flatten into a usable PostHog
    // property — see docs/marketing/listing-performance.md "Known gaps".
    config_id: id,
    location_id: location.id,
    ...enrichment,
    ...geo,
    locationType,
    spaceType,
    price,
    currency: 'MXN',
    sqm,
    pillar: pillarPage,
    // How many photos this listing actually has. Flat on `view_item` so a
    // PostHog funnel can segment listing-page → lead/checkout conversion by
    // photo count without a listing-side join. Until this shipped the only
    // event carrying a photo count was `listing_gallery_grid_more_click`,
    // which fires ONLY on desktop, ONLY on listings with >5 photos, and ONLY
    // when the badge is clicked — far too biased to answer "do more photos
    // convert better?".
    photo_count: location.photos?.length ?? 0
  }

  saveProductContext({
    vertical: enrichment.vertical,
    product_type: enrichment.product_type,
    deal_type: enrichment.deal_type,
    locationType: locationType ?? undefined,
    locationId: location.id,
    configurationId: id,
    price
  })

  pushToDataLayer('spotme_view_item', payload)

  ga.event('view_item', {
    ...payload,
    items: [
      {
        item_id: id,
        item_name: title,
        item_category: enrichment.vertical,
        price,
        quantity: 1
      }
    ]
  })
  fbp.event('ViewContent', {
    event_id: eventId,
    content_ids: [id],
    content_type: 'product',
    content_category: enrichment.vertical,
    contents: [{ id, quantity: config.availability || 1 }],
    currency: 'MXN',
    value: price,
    content_name: title
  })
  ms.event('view_item', {
    category: locationType ?? '',
    label: title,
    value: price?.toString()
  })
  oaiq.contents('contents_viewed', {
    amount: price,
    contents: oaiq.listingContent({
      id,
      name: title,
      vertical: enrichment.vertical,
      price
    }),
    eventId
  })
}

// ---------------------------------------------------------------------------
// 2. add_to_cart
// ---------------------------------------------------------------------------

type AddToCartSubEvent =
  | 'broker_contact'
  | 'traditional_contact'
  | 'begin_checkout'
  | 'contact_wa'
  | 'chat_host'
  | 'technical_report'
  | 'on_demand'

type AddToCartParams = {
  subEvent: AddToCartSubEvent
  /** Required for listing-attached events; undefined for the listing-less
   * `on_demand` lead-form variant. */
  location?: ILocation
  config: Maybe<IConfiguration> | undefined
  spaceType: string
  quantity?: number
  storageTime?: StorageTime | ''
  /** City slug — used for listing-less variants like `on_demand` where geo
   * normally comes from `location.addressDetails`. */
  city?: string
  /** Pillar slug override — used when there is no `config.pillarPage`
   * (e.g. `on_demand` → `'storage-ondemand'`). */
  pillar?: string
}

export const addToCart = ({
  subEvent,
  location,
  config,
  spaceType,
  quantity = 1,
  storageTime,
  city,
  pillar
}: AddToCartParams) => {
  if (isInternalTraffic()) return
  const eventId = getEventId()
  const { price, availability, size } = config ?? {}
  const title = `${size?.length}x${size?.width} - ${spaceType}`
  const tdv = getTotalDealValue(price ?? 0, quantity, storageTime)
  const pillarHint = config?.pillarPage ?? pillar
  const enrichment = buildEnrichment(config as IConfiguration, {
    storageTime,
    quantity,
    monthlyPrice: price ?? 0,
    pillar: pillarHint,
    ...(subEvent === 'on_demand' ? { subType: 'on_demand' as const } : {})
  })
  // For listing-less variants (`on_demand`) the form already knows the city —
  // fall back to that when `location` is undefined.
  const geo = location
    ? geoFields(location)
    : { city: city ? norm(city) : undefined, state: undefined, hood: undefined }

  const payload = {
    event_id: eventId,
    sub_event: subEvent,
    // Flat config_id (the configuration UUID) so every CTA sub_event
    // (broker_contact / traditional_contact / contact_wa / begin_checkout /
    // chat_host / technical_report) is sliceable per listing in PostHog and
    // joins to view_item + the search-card events on the same key. Undefined
    // for the listing-less `on_demand` variant (config is null there).
    config_id: config?.id,
    location_id: location?.id,
    ...enrichment,
    ...geo,
    locationType: config?.locationType,
    spaceType,
    value: tdv,
    monthly_price: price ?? 0,
    units_requested: quantity,
    currency: 'MXN',
    sqm: Math.round((size?.length ?? 0) * (size?.width ?? 0)),
    pillar: config?.pillarPage ?? pillar
  }

  saveProductContext({
    vertical: enrichment.vertical,
    product_type: enrichment.product_type,
    deal_type: enrichment.deal_type,
    locationType: config?.locationType ?? undefined,
    locationId: location?.id,
    configurationId: config?.id ?? undefined,
    price: price ?? 0,
    quantity,
    storageTime
  })

  pushToDataLayer('spotme_add_to_cart', payload)

  ga.event(subEvent, payload)
  ga.event('add_to_cart', {
    items: [
      {
        item_id: config?.id,
        item_name: title,
        item_category: enrichment.vertical,
        price,
        quantity
      }
    ],
    ...payload
  })
  ms.event('add_to_cart', {
    category: spaceType ?? '',
    label: title,
    value: tdv.toString()
  })
  fbp.event('AddToCart', {
    event_id: eventId,
    content_ids: [config?.id],
    content_type: 'product',
    content_category: enrichment.vertical,
    contents: [{ id: config?.id, quantity: availability || 1 }],
    currency: 'MXN',
    value: tdv,
    content_name: title
  })

  // Engagement-level Meta `Lead` — fires alongside `AddToCart` so the
  // single retargeting audience on `Lead` picks up every demand-side
  // engagement (broker_contact / traditional_contact / contact_wa /
  // begin_checkout / chat_host / technical_report / on_demand) across
  // every vertical (storage + parking) and every deal type. Lower-intent
  // than the `Lead` fires inside `generateLead` / `completeKyc` /
  // `purchase` / `onDemandLead` — that's fine for RETARGETING (the goal
  // is audience reach), but DO NOT set bare `Lead` as a Meta campaign
  // OPTIMIZATION goal — the volume mix will retrain the bidder downward
  // and dilute the high-intent fires above. For optimization, use a
  // Custom Conversion in Events Manager filtered by `content_name`
  // (engagement sub_event) or `content_category` (deal_type).
  fbp.event('Lead', {
    event_id: eventId,
    content_category: enrichment.deal_type ?? 'monthly',
    content_name: subEvent,
    sub_event: subEvent,
    ...(enrichment.vertical ? { vertical: enrichment.vertical } : {}),
    ...(tdv ? { value: tdv, currency: 'MXN' } : {})
  })

  // Segmented WhatsApp click — fires alongside `contact_wa` so LinkedIn /
  // remarketing audiences keep a stable event name. `qualified_whatsapp_lead`
  // used to fire here too, but it was a misnomer — clicking the WA button is
  // not the same as actually sending a qualified message. It now fires
  // SERVER-SIDE from `spotme-server/src/LangGraphAgent/agent.ts` (complete
  // node) when LangGraph creates a HubSpot lead from a real WA conversation.
  // Closed loop: this click embeds a hashed PostHog session id in the
  // outbound prefill via `buildLeadFormWaMessage`; the backend extracts it
  // on first inbound and fires `qualified_whatsapp_lead` with the matching
  // distinctId so PostHog stitches the conversion back to this browser
  // session. See `spotme-client/src/components/LeadForm/SpecialistCard.tsx`.
  if (subEvent === 'contact_wa') {
    fireSegmentedEvent('listing_whatsapp_click', payload)
  }

  // Vertical / vertical+dealtype segmented add_to_cart events. Fired alongside
  // the bare sub-event so Google Ads (and Meta) can configure campaign-specific
  // Secondary goals per vertical/dealtype without slicing by parameter.
  // See ATC_VERTICAL_NAMES / ATC_DEALTYPE_NAMES for the matrix.
  //
  // Derive the slug from `enrichment.deal_type` (already pillar-aware via
  // the canonical `getEffectiveDealType` called inside `buildEnrichment`)
  // so a sub-$30k bodega/nave listing routes to `contact_wa_storage_broker`
  // instead of `contact_wa_storage_now`. The former is excluded from
  // TIER_ROLLUP_MAP — the latter would pollute `storage_secondary` and
  // re-incentivize PMax storage bidding on broker inventory.
  const dealTypeSlug: DealTypeSlug =
    enrichment.deal_type === 'monthly' ? 'now' : enrichment.deal_type
  oaiq.contents('items_added', {
    amount: tdv,
    contents: oaiq.listingContent({
      id: config?.id,
      name: title,
      vertical: enrichment.vertical,
      price,
      quantity
    }),
    eventId
  })

  const segmentedAtcEvents = getSegmentedAtcEvents(
    subEvent,
    enrichment.vertical,
    dealTypeSlug
  )
  for (const evt of segmentedAtcEvents) {
    fireSegmentedEvent(evt, payload)
  }
}

// ---------------------------------------------------------------------------
// 3. sign_up
// ---------------------------------------------------------------------------

type SignUpParams = {
  method: 'phone' | 'email' | 'google' | 'google_popup' | 'facebook' | 'apple'
}

export const signUp = ({ method }: SignUpParams) => {
  if (isInternalTraffic()) return
  const eventId = getEventId()

  const lastSeen = getLastSeenLocation()

  const payload = {
    event_id: eventId,
    method,
    ...(lastSeen
      ? {
          vertical: lastSeen.vertical,
          product_type: lastSeen.product_type,
          ...(lastSeen.deal_type ? { deal_type: lastSeen.deal_type } : {}),
          ...(lastSeen.locationType
            ? { locationType: lastSeen.locationType }
            : {})
        }
      : {})
  }

  pushToDataLayer('spotme_sign_up', payload)

  ga.event('sign_up', payload)
  ms.event('sign_up', { category: 'sign_up', label: method })
  linkedin.trackConversion('sign_up')
  oaiq.customerAction('registration_completed', { eventId })
}

// ---------------------------------------------------------------------------
// 3b. auth funnel — step events + auth_error
// ---------------------------------------------------------------------------
//
// The login funnel is measured end to end with these events plus the existing
// `auth_modal_open` / `auth_modal_abandoned` (entry, fired in AuthProvider) and
// `login` / `sign_up` (success). Every event carries `surface` (modal vs the
// standalone /login page); most carry `method`. Mobile-vs-desktop needs no
// param — PostHog (`$device_type`) and GA4 capture it on every event. These are
// internal funnel/diagnostic signals: never rolled up, never bid on.

type AuthMethod = 'phone' | 'email' | 'google' | 'facebook' | 'google_popup'

type AuthSurface = 'modal' | 'page'

/**
 * Where the auth funnel is running. The Login tree renders in exactly two
 * places: the global modal (over any page) and the standalone /login and
 * /authenticate pages — so the pathname disambiguates them without threading a
 * prop through every step component. Callers on their own pages (verification /
 * reset landings) pass `surface` explicitly.
 */
const getAuthSurface = (): AuthSurface => {
  if (typeof window === 'undefined') return 'modal'
  const path = window.location.pathname
  return path === '/login' || path === '/authenticate' ? 'page' : 'modal'
}

export type AuthFunnelEvent =
  | 'auth_method_select'
  | 'auth_phone_submit'
  | 'auth_otp_sent'
  | 'auth_otp_submit'
  | 'auth_otp_resend'
  | 'auth_email_continue'
  | 'auth_email_exists'
  | 'auth_password_submit'
  | 'auth_register_submit'
  | 'auth_verification_sent'
  | 'auth_verification_completed'
  | 'auth_reset_requested'
  | 'auth_reset_completed'

type AuthEventParams = {
  /** Overrides the pathname-derived surface (e.g. the verification/reset pages). */
  surface?: AuthSurface
  method?: AuthMethod
  /** auth_email_exists only: true = returning user (login), false = new (register). */
  exists?: boolean
}

/**
 * Fire a login-funnel step event. `surface` defaults to the pathname-derived
 * value so most call sites pass only the parts that vary.
 */
export const authEvent = (
  name: AuthFunnelEvent,
  { surface, method, exists }: AuthEventParams = {}
) => {
  if (isInternalTraffic()) return

  ga.event(name, {
    surface: surface ?? getAuthSurface(),
    ...(method ? { method } : {}),
    ...(exists === undefined ? {} : { exists })
  })
}

type AuthErrorParams = {
  method: AuthMethod
  /** Firebase error code, e.g. `auth/popup-blocked`, `auth/internal-error`. */
  code?: string
  /** Whether the browser was detected as an in-app browser (webview). */
  inApp?: boolean
}

/**
 * Auth sign-in FAILURE (system/unexpected, not user-input). Fires from the
 * `.catch` of each provider flow with the Firebase error `code` so breakage —
 * especially the in-app-browser / storage-partition failure ("missing initial
 * state") that is otherwise invisible because it happens off-domain on
 * `firebaseapp.com` — shows up in PostHog + GA4. Expected user errors (wrong
 * password, invalid/expired OTP, rate limit, popup-closed-by-user) are NOT
 * reported here — the submit→success funnel already captures those drop-offs.
 */
export const authError = ({ method, code, inApp }: AuthErrorParams) => {
  if (isInternalTraffic()) return

  ga.event('auth_error', {
    method,
    ...(code ? { code } : {}),
    ...(inApp === undefined ? {} : { in_app: inApp })
  })
}

// ---------------------------------------------------------------------------
// 4. begin_checkout
// ---------------------------------------------------------------------------

type BeginCheckoutParams = {
  location: ILocation
  config: IConfiguration
  spaceType: string
  storageTime?: StorageTime | ''
  quantity?: number
}

export const beginCheckout = ({
  location,
  config,
  spaceType,
  storageTime,
  quantity = 1
}: BeginCheckoutParams) => {
  if (isInternalTraffic()) return
  const eventId = getEventId()
  const { price, availability, size, id } = config
  const title = `${size?.length}x${size?.width} - ${spaceType}`
  const tdv = getTotalDealValue(price ?? 0, quantity, storageTime)
  const enrichment = buildEnrichment(config, {
    storageTime,
    quantity,
    monthlyPrice: price ?? 0
  })
  const geo = geoFields(location)

  const payload = {
    event_id: eventId,
    ...enrichment,
    ...geo,
    locationType: config.locationType,
    spaceType,
    value: tdv,
    monthly_price: price ?? 0,
    units_requested: quantity,
    currency: 'MXN'
  }

  saveProductContext({
    vertical: enrichment.vertical,
    product_type: enrichment.product_type,
    deal_type: enrichment.deal_type,
    locationType: config.locationType ?? undefined,
    locationId: location.id,
    configurationId: config.id,
    price: price ?? 0,
    quantity,
    storageTime
  })

  pushToDataLayer('spotme_begin_checkout', payload)

  ga.event('begin_checkout', {
    ...payload,
    items: [
      {
        item_id: id,
        item_name: title,
        item_category: enrichment.vertical,
        price,
        quantity
      }
    ]
  })
  fbp.event('InitiateCheckout', {
    event_id: eventId,
    content_ids: [id],
    content_type: 'product',
    content_category: enrichment.vertical,
    contents: [{ id, quantity: availability || 1 }],
    currency: 'MXN',
    value: tdv,
    content_name: title
  })
  ms.event('begin_checkout', {
    category: spaceType,
    label: title,
    value: tdv.toString()
  })
  linkedin.trackConversion('begin_checkout')
  oaiq.contents('checkout_started', {
    amount: tdv,
    contents: oaiq.listingContent({
      id,
      name: title,
      vertical: enrichment.vertical,
      price,
      quantity
    }),
    eventId
  })
}

/**
 * /reservar page-mount event for the unauthed Monthly cohort. Pairs with
 * `begin_checkout` (which fires for authed Monthly via PriceCard CTA) —
 * splitting them lets PostHog / GA4 funnels measure both paths cleanly
 * without double-counting the same conversion intent.
 *
 * Fires the bare `begin_checkout_preauth` event (keeps existing dashboards
 * + PostHog filters intact) and, when the listing's locationType resolves
 * to storage or parking, the vertical-segmented variant
 * `begin_checkout_preauth_storage` / `_parking`. The segmented variant is
 * what feeds the `storage_secondary` / `parking_secondary` tier rollup
 * for Google Ads campaign goal optimization — see TIER_ROLLUP_MAP above.
 *
 * Server-side gate at /reservar locks dealType=Monthly + locationType to
 * SelfStorageUnit | Parking, so the vertical lookup is virtually always
 * resolved. The `other` fallback exists for safety but shouldn't fire in
 * production.
 */
export const beginCheckoutPreauth = (params: {
  locationId: string
  configurationId: string
  locationType?: LocationType | null
  city?: string | null
}) => {
  if (isInternalTraffic()) return

  const payload = {
    locationId: params.locationId,
    configurationId: params.configurationId,
    locationType: params.locationType,
    city: params.city
  }

  // Bare event — non-rollup, preserves existing funnel dashboards.
  ga.event('begin_checkout_preauth', payload)

  const vertical = getVertical(params.locationType)
  if (vertical === 'storage') {
    fireSegmentedEvent('begin_checkout_preauth_storage', payload)
  } else if (vertical === 'parking') {
    fireSegmentedEvent('begin_checkout_preauth_parking', payload)
  }
}

// ---------------------------------------------------------------------------
// 5. complete_kyc
// ---------------------------------------------------------------------------

type CompleteKycParams = {
  /** Enrichment from the listing the user was viewing, if available. */
  enrichment?: Partial<EnrichedEventParams>
  /** User info — used as fallback when no product context is available. */
  customerType?: CustomerType | null
  interest?: Interest | null
}

/**
 * Infer a default vertical from user intent when no listing context exists.
 * Most renters on SpotMe are looking for storage — use it as the default.
 * Hosts don't map to a product vertical for conversion purposes.
 */
const inferVerticalFromUser = (
  customerType?: CustomerType | null,
  interest?: Interest | null
): { vertical: Vertical; product_type: ProductType } | null => {
  if (interest === Interest.Host) return null
  if (
    customerType === CustomerType.RentIndividual ||
    customerType === CustomerType.RentBroker ||
    interest === Interest.Rent
  ) {
    // Default to storage — ~70% of renter traffic
    return { vertical: 'storage', product_type: 'mini_bodega' }
  }
  return null
}

export type KycAnalyticsContext = {
  vertical?: 'storage' | 'parking'
  deal_type?: AnalyticsDealType
}

/**
 * Vertical / deal-type slice for the PostHog-only `kyc_step` funnel event
 * (`UserConfirmation/layout.tsx`). Walks the SAME fallback chain
 * `completeKyc` uses — active product context → last-seen listing → renter
 * inference — but read-only: it never consumes the product context, so the
 * terminal `complete_kyc` still gets its full enrichment. Until 2026-09-14
 * `kyc_step` carried only `{ step, user_id }`, so the KYC funnel could not be
 * split storage vs parking. `vertical` is omitted (never `'other'`) for hosts
 * and unmapped types so a PostHog breakdown has no third bucket.
 */
export const resolveKycAnalyticsContext = (
  customerType?: CustomerType | null,
  interest?: Interest | null
): KycAnalyticsContext => {
  const ctx =
    peekProductContext() ??
    getLastSeenLocation() ??
    inferVerticalFromUser(customerType, interest)
  if (!ctx) return {}
  // `inferVerticalFromUser` has no deal_type; the stored contexts may.
  const dealType: AnalyticsDealType | undefined = (ctx as ProductContext)
    .deal_type
  return {
    ...(ctx.vertical === 'storage' || ctx.vertical === 'parking'
      ? { vertical: ctx.vertical }
      : {}),
    ...(dealType ? { deal_type: dealType } : {})
  }
}

export const completeKyc = (params?: CompleteKycParams) => {
  if (isInternalTraffic()) return
  const eventId = getEventId()

  // Priority chain: active product context > last seen location > user inference
  const ctx = consumeProductContext()
  const lastSeen = !ctx ? getLastSeenLocation() : null
  const effectiveCtx = ctx ?? lastSeen

  // Build value from stored context if available
  const tdv =
    effectiveCtx?.price && effectiveCtx.price > 0
      ? getTotalDealValue(
          effectiveCtx.price,
          effectiveCtx.quantity,
          effectiveCtx.storageTime
        )
      : undefined

  const payload = {
    event_id: eventId,
    ...(params?.enrichment ?? {}),
    ...(effectiveCtx
      ? {
          vertical: effectiveCtx.vertical,
          product_type: effectiveCtx.product_type,
          ...(effectiveCtx.deal_type
            ? { deal_type: effectiveCtx.deal_type }
            : {}),
          ...(effectiveCtx.locationType
            ? { locationType: effectiveCtx.locationType }
            : {}),
          ...(effectiveCtx.price ? { monthly_price: effectiveCtx.price } : {}),
          ...(tdv ? { value: tdv, currency: 'MXN' } : {})
        }
      : {})
  }

  pushToDataLayer('spotme_complete_kyc', payload)

  ga.event('complete_kyc', payload)
  fbp.trackCustom('CompleteKYC', payload)
  fbp.event('CompleteRegistration', { event_id: eventId, status: true })
  // Unified `Lead` standard event so retargeting can optimize on `Lead` and
  // pick up KYC completions across all on-platform funnels (storage, parking,
  // parking_b2b). Pulls funnel category from the listing context the user came
  // from — broker-tier rentals get `content_category='broker'`, default is
  // 'monthly' for the standard SpotMe Now path.
  fbp.event('Lead', {
    event_id: eventId,
    content_category: effectiveCtx?.deal_type ?? 'monthly',
    content_name: 'complete_kyc',
    ...(effectiveCtx?.vertical ? { vertical: effectiveCtx.vertical } : {}),
    ...(tdv ? { value: tdv, currency: 'MXN' } : {})
  })
  // Meta CAPI mirror — same eventId dedupes with the browser pixel
  // above, so iOS-ATT and ad-blocker traffic still counts toward the
  // unified Lead audience. See src/utils/pixels/capi.ts for the
  // engagement-vs-high-intent scoping rule.
  recordBrowserLeadCapi({
    eventId,
    contentCategory: effectiveCtx?.deal_type ?? 'monthly',
    contentName: 'complete_kyc',
    vertical: effectiveCtx?.vertical,
    value: tdv,
    currency: tdv ? 'MXN' : undefined
  })
  ms.event('complete_kyc', {
    category: 'complete_kyc',
    label: 'email_verified',
    value: tdv?.toString()
  })
  linkedin.trackConversion('complete_kyc')

  // Segmented conversion events — prefer explicit enrichment > stored context > user inference
  const resolvedCtx =
    params?.enrichment?.vertical && params?.enrichment?.product_type
      ? {
          vertical: params.enrichment.vertical as Vertical,
          product_type: params.enrichment.product_type as ProductType
        }
      : (effectiveCtx ??
        inferVerticalFromUser(params?.customerType, params?.interest))

  if (resolvedCtx) {
    const segmented = getSegmentedEvents(
      'complete_kyc',
      resolvedCtx.vertical,
      resolvedCtx.product_type,
      effectiveCtx?.quantity
    )
    for (const evt of segmented) {
      fireSegmentedEvent(evt, payload)
    }
  }
}

// ---------------------------------------------------------------------------
// 6. generate_lead
// ---------------------------------------------------------------------------

export type GenerateLeadParams = {
  method:
    | 'business'
    | 'business_contact'
    | 'business_hero'
    | 'business_fleet'
    | 'calculate'
    | 'search_empty'
    | 'contact_form'
    | 'broker'
    | 'pillar_specialist'
    | 'traditional_hero'
    | 'traditional_contact'
    // Soluciones Logísticas (3PL) — observed, never bid on. `logistics_3pl`
    // is the hero form submit; `logistics_hero` is the WA escape-hatch
    // click, always fired with `intentOnly: true` (see spotmeEvents body).
    | 'logistics_3pl'
    | 'logistics_hero'
    // Pre-auth /reservar phone-submit lead. Fires the moment the renter
    // taps "Confirmar reserva" — BEFORE the OTP completes — so even bail-
    // outs at the OTP step still attribute. Always Monthly B2C
    // (storage/parking); the page route 404s for any other dealType.
    | 'preauth_reservar'
  locationType?: LocationType
  dealType?: Maybe<DealType>
  storageTime?: StorageTime | ''
  quantity?: number
  monthlyPrice?: number
  /**
   * Which channel the lead actually completed on. Powers the unified
   * listing-conversion lead form so a single Meta `Lead` event can be
   * sliced by WA-vs-email funnel. Optional — when omitted, the event
   * lands without a channel param (legacy callers).
   */
  channel?: 'whatsapp' | 'email'
  /**
   * When true, this fire represents bare upstream INTENT (a WhatsApp-pill
   * click — see PriceCard.handleOnWhatsappCta) rather than a completed or
   * qualified lead. It short-circuits every segmented `*_lead` /
   * `*_qualified_lead` / `*_form_submit` conversion ENDPOINT — the events that
   * roll into the Google Ads primary/secondary tier goals via TIER_ROLLUP_MAP —
   * because a click is NOT a lead. The click's intent is still captured as a
   * secondary `*_contact_*` event by the caller's `handleAddToCart`, and the
   * true WhatsApp PRIMARY is the server-side `qualified_whatsapp_lead` fired
   * from LangGraph when a HubSpot deal is created. Broad retargeting signals
   * (Meta `Lead`, CAPI, base `generate_lead`, LinkedIn) still fire.
   */
  intentOnly?: boolean
}

/** Maps generate_lead method to the host page it fires from. */
const METHOD_TO_PAGE_TYPE: Record<string, string> = {
  business: 'negocio',
  business_contact: 'negocio',
  // `business_hero` + `business_fleet` both fire from /flotillas (the
  // left-rail WA escape hatch and the canonical form submit respectively).
  // See FlotillasHero.tsx comment block for the supply/demand split.
  business_hero: 'flotillas',
  business_fleet: 'flotillas',
  traditional_hero: 'tradicional',
  traditional_contact: 'tradicional',
  calculate: 'calculate',
  broker: 'tradicional',
  logistics_3pl: 'soluciones-logisticas',
  logistics_hero: 'soluciones-logisticas',
  preauth_reservar: 'reservar',
  // Demand-side specialist form on B2B pillar pages
  // (/renta-bodegas-comerciales, /renta-naves-industriales). Routes to the
  // SpotMe Pro broker funnel like the other broker methods, but `page_type`
  // and `content_name` reflect the originating pillar surface so Meta/GA4
  // reporting can attribute leads to the pillar pages instead of the
  // tradicional supply page. The pillar (warehouse vs industrial) is still
  // distinguishable via the `product_type` enrichment field.
  pillar_specialist: 'pillar_b2b_specialist'
}

/**
 * Maps each `generateLead` method to the deal-type funnel category sent to
 * Meta as `content_category`. Drives retargeting unification: a single `Lead`
 * standard event + a Custom Conversion filtered by `content_category` per
 * funnel (monthly / broker / traditional) — see Meta Events Manager.
 *
 * Form-only methods without a listing default to a sensible funnel:
 *   - business_* / calculate / broker → 'broker'        (off-platform sales-led)
 *   - traditional_*                   → 'traditional'   (Anexo C)
 *   - search_empty / contact_form     → 'monthly'       (renter intent default)
 */
const METHOD_TO_LEAD_CATEGORY: Record<
  GenerateLeadParams['method'],
  AnalyticsDealType
> = {
  business: 'broker',
  business_contact: 'broker',
  business_hero: 'broker',
  business_fleet: 'broker',
  calculate: 'broker',
  broker: 'broker',
  pillar_specialist: 'broker',
  traditional_hero: 'traditional',
  traditional_contact: 'traditional',
  search_empty: 'monthly',
  contact_form: 'monthly',
  // Soluciones Logísticas (3PL) is B2B Pro-pipeline territory — off-platform,
  // specialist-facilitated, same funnel category as broker/business.
  logistics_3pl: 'broker',
  logistics_hero: 'broker',
  // /reservar is the SpotMe Now (Monthly) pre-auth surface. Server-side
  // gate at `pages/[pillar]/[city]/[hood]/[configId]/reservar.tsx` 404s
  // any non-Monthly dealType, so `monthly` is the only correct category.
  preauth_reservar: 'monthly'
}

/**
 * Vertical fallback per `generateLead` method when no `locationType` is
 * supplied. Lets PostHog/Meta/GA4 still slice form-only methods by vertical.
 * - `business_fleet` / `business_hero` are parking (Flotillas — fleet form
 *   submit and left-rail WA escape hatch respectively).
 * - Other `business_*` and `calculate` are B2B storage by default (negocio).
 * - `broker` / `pillar_specialist` lean storage (B2B pillar specialist CTA).
 * - `traditional_*` is traditional B2C storage.
 * - `search_empty` / `contact_form` stay undefined — surface-agnostic.
 * - `logistics_3pl` / `logistics_hero` are DELIBERATELY OMITTED (see spec
 *   §3.3, "3PL observed never bid on"). Leaving `enrichment.vertical`
 *   undefined for logistics keeps the lead OUT of the `storage_qualified_lead`
 *   / `parking_b2b_qualified_lead` branches below — a 3PL lead must NOT
 *   masquerade as a storage/parking qualified lead.
 */
const METHOD_TO_VERTICAL_FALLBACK: Partial<
  Record<GenerateLeadParams['method'], Vertical>
> = {
  business: 'storage',
  business_contact: 'storage',
  business_hero: 'parking',
  business_fleet: 'parking',
  calculate: 'storage',
  broker: 'storage',
  pillar_specialist: 'storage',
  traditional_hero: 'storage',
  traditional_contact: 'storage'
}

// Vertical-scoped Traditional lead event — bare `traditional_lead` is
// vertical-agnostic (mixes storage + parking Traditional), so it is kept
// OUT of TIER_ROLLUP_MAP; these segmented variants land in storage_primary /
// parking_primary. Mirrors broker_lead_storage / broker_lead_parking. The
// broker split is inline; Traditional uses an exported helper purely to give
// the firing decision a unit-test seam — and the LIVE generateLead branch
// below calls THIS helper, so the test exercises the real decision (not a
// dead parallel). Returns null only for 'other', which is unreachable for
// method='traditional_contact' (see call site).
export const traditionalLeadEventForVertical = (
  vertical: Vertical
): 'traditional_lead_storage' | 'traditional_lead_parking' | null => {
  if (vertical === 'storage') return 'traditional_lead_storage'
  if (vertical === 'parking') return 'traditional_lead_parking'
  return null
}

export const generateLead = ({
  method,
  locationType,
  dealType,
  storageTime,
  quantity,
  monthlyPrice,
  channel,
  intentOnly
}: GenerateLeadParams) => {
  if (isInternalTraffic()) return
  const eventId = getEventId()

  const enrichment: Partial<EnrichedEventParams> = {
    deal_type: getAnalyticsDealType(dealType, monthlyPrice)
  }
  if (locationType) {
    enrichment.vertical = getVertical(locationType)
    enrichment.product_type = getProductType(locationType, dealType)
  } else if (METHOD_TO_VERTICAL_FALLBACK[method]) {
    enrichment.vertical = METHOD_TO_VERTICAL_FALLBACK[method]
  }

  const tdv = monthlyPrice
    ? getTotalDealValue(monthlyPrice, quantity, storageTime)
    : undefined

  const payload = {
    event_id: eventId,
    method,
    ...enrichment,
    ...(METHOD_TO_PAGE_TYPE[method]
      ? { page_type: METHOD_TO_PAGE_TYPE[method] }
      : {}),
    ...(storageTime ? { storage_time: storageTime } : {}),
    ...(quantity ? { units_requested: quantity } : {}),
    ...(monthlyPrice ? { monthly_price: monthlyPrice } : {}),
    ...(tdv ? { value: tdv, currency: 'MXN' } : {}),
    ...(channel ? { channel } : {}),
    // Marks this fire as upstream CLICK INTENT, not a captured lead. Load-bearing
    // for the leads KPI: the bare `generate_lead` below fires even when
    // `intentOnly` is set (retargeting wants it), and `LEAD_EVENTS` in
    // `leadsKpi/aggregate.ts` counts `generate_lead` — so without this flag a
    // WhatsApp-pill CLICK is indistinguishable from a captured lead and inflates
    // every denominator. It cannot be inferred from `method` + `channel`: the
    // LeadForm's own WhatsApp submit button (Step2Contact) fires the SAME method
    // with `channel: 'whatsapp'` and IS a real captured lead. Only the caller
    // knows which it is. Never drop this field.
    ...(intentOnly ? { intent_only: true } : {})
  }

  pushToDataLayer('spotme_generate_lead', payload)

  ga.event('generate_lead', payload)
  // Standard `Lead` event with a deal-type funnel category. Lets a single
  // retargeting campaign optimize on `Lead` (high-volume standard event with
  // pre-trained ML priors), while a Custom Conversion in Events Manager can
  // slice by funnel via `content_category` when reporting needs it.
  fbp.event('Lead', {
    event_id: eventId,
    content_category: METHOD_TO_LEAD_CATEGORY[method] ?? 'monthly',
    content_name: METHOD_TO_PAGE_TYPE[method] ?? method,
    ...(enrichment.vertical ? { vertical: enrichment.vertical } : {}),
    ...(enrichment.customer_type
      ? { customer_type: enrichment.customer_type }
      : {}),
    ...(quantity ? { units_requested: quantity } : {}),
    ...(tdv ? { value: tdv, currency: 'MXN' } : {})
  })
  // Meta CAPI mirror — see capi.ts for the high-intent-only scoping.
  recordBrowserLeadCapi({
    eventId,
    contentCategory: METHOD_TO_LEAD_CATEGORY[method] ?? 'monthly',
    contentName: METHOD_TO_PAGE_TYPE[method] ?? method,
    vertical: enrichment.vertical,
    value: tdv,
    currency: tdv ? 'MXN' : undefined
  })
  ms.event('generate_lead', {
    category: 'generate_lead',
    label: method,
    value: tdv?.toString()
  })
  linkedin.trackConversion('generate_lead')
  fbp.event('CompleteRegistration', { event_id: eventId, status: true })

  // Bare-intent short-circuit (WhatsApp-pill click — PriceCard.handleOnWhatsappCta
  // passes `intentOnly: true`). Everything below this line fires a segmented
  // `*_lead` / `*_qualified_lead` / `*_form_submit` conversion ENDPOINT that rolls
  // into the Google Ads primary/secondary tier goals via TIER_ROLLUP_MAP. A raw
  // WhatsApp click is upstream INTENT, not a completed lead — it's already recorded
  // as a secondary `*_contact_*` event via the caller's handleAddToCart, and the
  // true WhatsApp PRIMARY is the server-side `qualified_whatsapp_lead` (LangGraph,
  // on HubSpot deal creation). Returning here keeps the broad retargeting fires
  // above (Meta `Lead`, CAPI, base `generate_lead`, LinkedIn, CompleteRegistration)
  // while excluding click intent from the Google Ads bid signal.
  if (intentOnly) return

  // Soluciones Logísticas (3PL) — observed, never bid on. `logistics_lead` is
  // NOT in TIER_ROLLUP_MAP; no vertical fallback so no storage/parking
  // qualified_lead fires. `logistics_hero` always passes intentOnly: true and
  // returns above, so it never reaches this branch.
  if (method === 'logistics_3pl') {
    fireSegmentedEvent('logistics_lead', payload)
  }

  // Funnel-specific form-completion events — clean campaign conversion goals
  // that mirror the bare broker_contact / traditional_contact add_to_cart events.
  //   broker_lead      → SpotMe Pro funnel endpoint (off-platform close)
  //   traditional_lead → Traditional funnel endpoint (off-platform close)
  if (method === 'broker' || method === 'pillar_specialist') {
    fireSegmentedEvent('broker_lead', payload)
    // Vertical-scoped Broker lead events — bare `broker_lead` is
    // vertical-agnostic (mixes storage Broker + parking Broker), which
    // prevents per-pillar Google Ads campaign optimization. The two
    // segmented variants land cleanly inside `storage_primary` /
    // `parking_primary` via TIER_ROLLUP_MAP.
    if (enrichment.vertical === 'storage') {
      fireSegmentedEvent('broker_lead_storage', payload)
    } else if (enrichment.vertical === 'parking') {
      fireSegmentedEvent('broker_lead_parking', payload)
    }
  }
  if (method === 'traditional_contact') {
    fireSegmentedEvent('traditional_lead', payload)
    // `enrichment.vertical` is always 'storage' | 'parking' here (real
    // locationType → getVertical, or METHOD_TO_VERTICAL_FALLBACK →
    // 'storage'). The `?? 'storage'` is a type-level guard for the
    // Partial<EnrichedEventParams> shape AND preserves the legacy
    // bare-`traditional_lead → storage_primary` default for any
    // (unreachable) listing-less case — so this can never roll into nothing.
    const segmented = traditionalLeadEventForVertical(
      enrichment.vertical ?? 'storage'
    )
    if (segmented) fireSegmentedEvent(segmented, payload)
  }
  // Pre-auth /reservar phone-submit — fire vertical-segmented variants so
  // Smart Bidding can optimize Storage and Parking Monthly campaigns
  // independently. /reservar is server-gated to dealType=Monthly +
  // locationType in (SelfStorageUnit | Parking), so `enrichment.vertical`
  // is always cleanly storage or parking when this branch fires. Lands in
  // `storage_primary` / `parking_primary` via TIER_ROLLUP_MAP — parallel
  // to qualified_whatsapp_lead_storage / _parking (also "phone in HubSpot"
  // milestones, also primary). See docs/marketing/analytics-reference.md
  // §"Pre-auth /reservar" for the full funnel mapping.
  if (method === 'preauth_reservar') {
    if (enrichment.vertical === 'storage') {
      fireSegmentedEvent('generate_lead_preauth_storage', payload)
    } else if (enrichment.vertical === 'parking') {
      fireSegmentedEvent('generate_lead_preauth_parking', payload)
    }
  }

  // Segmented conversion events for storage leads (traditional + broker)
  if (enrichment.vertical === 'storage') {
    fireSegmentedEvent('storage_qualified_lead', payload)
  }

  // Segmented conversion events for B2B parking leads
  if (enrichment.product_type === 'parking_b2b') {
    fireSegmentedEvent('parking_b2b_qualified_lead', payload)
  }

  const B2B_METHODS = [
    'business',
    'business_contact',
    'business_hero',
    'business_fleet'
  ]
  if (B2B_METHODS.includes(method)) {
    fireSegmentedEvent('parking_b2b_form_submit', payload)
    // Treat any B2B-intent action (flotillas form/WhatsApp/hero CTA) as a
    // qualified lead for optimization, regardless of whether a specific
    // listing (and thus product_type) was attached. Guarded to avoid
    // double-firing when both the product_type and the B2B-method branch
    // would match on the same lead.
    if (enrichment.product_type !== 'parking_b2b') {
      fireSegmentedEvent('parking_b2b_qualified_lead', payload)
    }
  }
}

// ---------------------------------------------------------------------------
// 7. purchase
// ---------------------------------------------------------------------------

type PurchaseParams = {
  transactionId: string
  location: ILocation
  config: IConfiguration
  spaceType: string
  quantity?: number
  storageTime?: StorageTime | ''
}

export const purchase = ({
  transactionId,
  location,
  config,
  spaceType,
  quantity = 1,
  storageTime
}: PurchaseParams) => {
  if (isInternalTraffic()) return
  const eventId = getEventId()
  const { price, availability, size, id } = config
  const title = `${size?.length}x${size?.width} - ${spaceType}`
  const tdv = getTotalDealValue(price ?? 0, quantity, storageTime)
  const enrichment = buildEnrichment(config, {
    storageTime,
    quantity,
    monthlyPrice: price ?? 0
  })
  const geo = geoFields(location)

  const payload = {
    event_id: eventId,
    transaction_id: transactionId,
    ...enrichment,
    ...geo,
    value: tdv,
    monthly_price: price ?? 0,
    units_requested: quantity,
    currency: 'MXN'
  }

  pushToDataLayer('spotme_purchase', payload)

  ga.event('purchase', {
    ...payload,
    items: [
      {
        item_id: id,
        item_name: title,
        item_category: enrichment.vertical,
        price,
        quantity
      }
    ]
  })
  fbp.event('Purchase', {
    event_id: eventId,
    content_ids: [id],
    content_type: 'product',
    content_category: enrichment.vertical,
    contents: [{ id, quantity: availability || 1 }],
    currency: 'MXN',
    value: tdv,
    content_name: title
  })
  // Unified `Lead` standard event ALONGSIDE `Purchase`. Different eventIDs so
  // Meta treats them as separate logical events (no double-count). `Purchase`
  // remains the revenue/ROAS signal; `Lead` keeps SpotMe Now monthly closes
  // in the same retargeting-optimization pool as broker/traditional/on-demand
  // /KYC closes — otherwise a campaign optimizing on `Lead` would miss the
  // on-platform funnel entirely. `content_category` carries the deal-type so
  // a Custom Conversion can still slice by funnel.
  const leadEventId = getEventId()
  fbp.event('Lead', {
    event_id: leadEventId,
    content_category: enrichment.deal_type,
    content_name: 'purchase',
    ...(enrichment.vertical ? { vertical: enrichment.vertical } : {}),
    currency: 'MXN',
    value: tdv
  })
  // Meta CAPI mirror — uses the standalone leadEventId (not the parent
  // purchase eventId) so Meta dedupes against the Lead browser fire
  // above, not against the Purchase fire.
  recordBrowserLeadCapi({
    eventId: leadEventId,
    contentCategory: enrichment.deal_type,
    contentName: 'purchase',
    vertical: enrichment.vertical,
    value: tdv,
    currency: 'MXN'
  })
  ms.event('purchase', {
    category: 'purchase',
    label: title,
    value: tdv.toString()
  })
  linkedin.trackConversion('purchase')
  oaiq.contents('order_created', {
    amount: tdv,
    contents: oaiq.listingContent({
      id,
      name: title,
      vertical: enrichment.vertical,
      price,
      quantity
    }),
    eventId
  })

  // Segmented conversion events for campaign-specific goals
  const segmented = getSegmentedEvents(
    'purchase',
    enrichment.vertical,
    enrichment.product_type,
    quantity
  )
  for (const evt of segmented) {
    fireSegmentedEvent(evt, payload)
  }
}

// ---------------------------------------------------------------------------
// 8. login
// ---------------------------------------------------------------------------

type LoginParams = {
  method: 'phone' | 'email' | 'google' | 'google_popup' | 'facebook' | 'apple'
}

export const login = ({ method }: LoginParams) => {
  if (isInternalTraffic()) return
  const eventId = getEventId()
  const page = typeof window !== 'undefined' ? window.location.pathname : ''

  const lastSeen = getLastSeenLocation()

  const payload = {
    event_id: eventId,
    method,
    page,
    ...(lastSeen
      ? {
          vertical: lastSeen.vertical,
          product_type: lastSeen.product_type,
          ...(lastSeen.deal_type ? { deal_type: lastSeen.deal_type } : {}),
          ...(lastSeen.locationType
            ? { locationType: lastSeen.locationType }
            : {})
        }
      : {})
  }

  pushToDataLayer('spotme_login', payload)

  ga.event('login', { method, page })
  ms.event('login', { category: 'login', label: method })
}

// ---------------------------------------------------------------------------
// Almacenamiento a Domicilio — funnel events
// ---------------------------------------------------------------------------
//
// Four storage-focused, on-demand-prefixed events covering the funnel stages
// the marketing team wires as Google Ads conversion goals / GA4 key events:
//
//   storage_on_demand_view         — page mount on the on-demand landing
//   storage_on_demand_lead_start   — first form-field interaction
//   storage_on_demand_lead         — form submit success (PRIMARY)
//   storage_on_demand_whatsapp     — any WhatsApp CTA on the on-demand surface
//
// Each event ships the same shape (`vertical: 'storage'`,
// `pillar: 'storage-ondemand'`, optional `city`) so Google Ads can slice or
// segment campaigns by city without per-event taxonomy tweaks.
//
// These are ADDITIVE: the existing `add_to_cart` with `subEvent: 'on_demand'`
// continues to fire on form submit so the server-side marketing-attribution
// pipeline (see CLAUDE.md "Marketing Attribution") and existing remarketing
// audiences keep working. The new events are the campaign-friendly equivalents
// for direct Google Ads goal wiring.

type OnDemandEventName =
  | 'storage_on_demand_view'
  | 'storage_on_demand_lead_start'
  | 'storage_on_demand_lead'
  | 'storage_on_demand_whatsapp'
  | 'storage_on_demand_card_view'
  | 'storage_on_demand_card_click'

type OnDemandEventInput = {
  /** Canonical on-demand city slug (e.g. `'monterrey'`, `'cdmx'`). Optional —
   *  the parent landing page fires `view` without a resolved city. */
  city?: string
  /** Which surface hosted the cross-link card. Only on the
   *  `_card_view` / `_card_click` discovery events. `directory_ficha` is the
   *  cross-sell card in a directory ficha's right rail (2026-08-24) — a
   *  recovery surface, so discovery-event rules apply unchanged (never in
   *  `TIER_ROLLUP_MAP`). */
  surface?: 'search_grid' | 'empty_state' | 'directory_ficha'
}

const fireOnDemandEvent = (
  eventName: OnDemandEventName,
  { city, surface }: OnDemandEventInput,
  options?: { metaLeadCategory?: AnalyticsDealType }
) => {
  if (isInternalTraffic()) return
  const eventId = getEventId()
  const payload = {
    event_id: eventId,
    vertical: 'storage' as const,
    pillar: 'storage-ondemand' as const,
    ...(city ? { city: norm(city) } : {}),
    ...(surface ? { surface } : {})
  }

  pushToDataLayer(`spotme_${eventName}`, payload)
  ga.event(eventName, payload)

  // Tier rollup — on-demand fires bypass `fireSegmentedEvent` (they have
  // their own dispatcher, see header above), so we apply the rollup
  // directly here. Same map, same recursion-safety as the fireSegmentedEvent
  // path. Keeps `storage_on_demand_lead` rolling up into `storage_primary`
  // and `storage_on_demand_whatsapp` into `storage_secondary` automatically.
  const rollup = TIER_ROLLUP_MAP[eventName]
  if (rollup) {
    pushToDataLayer(`spotme_${rollup}`, payload)
    ga.event(rollup, payload)
  }

  // Unified Meta `Lead` standard event for on-demand close events. Lets a
  // single retargeting campaign optimize on `Lead` and a Custom Conversion
  // slice the on-demand funnel via `content_category` (the on-demand funnel
  // routes through the Traditional specialist hand-off — see header above).
  if (options?.metaLeadCategory) {
    fbp.event('Lead', {
      event_id: eventId,
      content_category: options.metaLeadCategory,
      content_name: eventName,
      vertical: 'storage',
      ...(city ? { city: norm(city) } : {})
    })
    // Meta CAPI mirror — only fires when the gating branch already
    // fires the browser Lead pixel (i.e. on-demand close events, not
    // view / lead_start / whatsapp).
    recordBrowserLeadCapi({
      eventId,
      contentCategory: options.metaLeadCategory,
      contentName: eventName,
      vertical: 'storage'
    })
  }

  // Meta `ViewContent` with `content_type: 'product'` + synthetic per-city
  // product ID. On-demand is a Service (not a real product catalog item),
  // but firing this as a "product view" unlocks Meta retargeting on the
  // same surfaces listings use — custom audiences keyed on the synthetic
  // content_id, and a future product-catalog feed can ingest these IDs
  // to enable full DPA without changing the fire shape. Stable per
  // (city, vertical) so the same visitor on the same city pulls the same
  // ID across sessions. Parent page (no city) → `ondemand-storage-mx`.
  if (eventName === 'storage_on_demand_view') {
    const slug = city ? norm(city) : 'mx'
    fbp.event('ViewContent', {
      event_id: eventId,
      content_ids: [`ondemand-storage-${slug}`],
      content_type: 'product',
      content_category: 'storage',
      content_name: `Almacenamiento a Domicilio${
        city ? ` — ${norm(city)}` : ''
      }`,
      currency: 'MXN'
    })
  }
}

/** Fires `storage_on_demand_view` on landing-page mount. */
export const onDemandView = (input: OnDemandEventInput = {}) =>
  fireOnDemandEvent('storage_on_demand_view', input)

/** Fires `storage_on_demand_lead_start` on the first interaction with the
 *  hero or full lead form (typically `onFocus` of the first field). */
export const onDemandLeadStart = (input: OnDemandEventInput = {}) =>
  fireOnDemandEvent('storage_on_demand_lead_start', input)

/** Fires `storage_on_demand_lead` on successful form submission, plus a
 *  unified Meta `Lead` standard event with `content_category='traditional'`
 *  so retargeting campaigns can optimize on `Lead` and pick up on-demand
 *  closes alongside broker/traditional/KYC closes. The existing
 *  `addToCart({ subEvent: 'on_demand' })` continues to fire as well. */
export const onDemandLead = (input: OnDemandEventInput = {}) =>
  fireOnDemandEvent('storage_on_demand_lead', input, {
    metaLeadCategory: 'traditional'
  })

/** Fires `storage_on_demand_whatsapp` on any WhatsApp CTA tap on the
 *  on-demand surface (hero ghost button, form WhatsApp link, recovery
 *  card WhatsApp). */
export const onDemandWhatsapp = (input: OnDemandEventInput = {}) =>
  fireOnDemandEvent('storage_on_demand_whatsapp', input)

/** Fires `storage_on_demand_card_view` when the search-injected on-demand
 *  cross-link card (`OnDemandSearchCard` grid slot / `OnDemandRecoveryCard`
 *  empty state) actually enters the viewport. Viewport-gated (callers use
 *  IntersectionObserver) + deduped once per (surface, city) per POSTHOG
 *  SESSION — mount-time fires would count cards inside the closed mobile
 *  drawer (`keepMounted`) and re-mounts on every filter change, the same
 *  inflation the LeadForm engagement pixel fix addressed (2026-05-19).
 *
 *  The dedupe key carries `posthog.sessionId()` (2026-09-14): a tab-scoped
 *  `sessionStorage` flag outlived PostHog's 30-min `$session_id` rollover,
 *  so a later click had no same-session view and card CTR was unmeasurable
 *  (63 view-less click sessions / 30d). See `onDemandCardViewKey.ts` for
 *  the pre-init fallback. Storage unavailable ⇒ fire undeduped (safeStorage
 *  rule). NOT a conversion: never add to TIER_ROLLUP_MAP — this is the
 *  discovery-funnel denominator (FUNNELS.md F6), observability only. */
export const onDemandCardView = (input: OnDemandEventInput = {}) => {
  const key = buildOnDemandCardViewKey({
    surface: input.surface,
    city: input.city,
    sessionId: posthog.sessionId()
  })
  if (safeStorageGet('session', key)) return
  safeStorageSet('session', key, '1')
  fireOnDemandEvent('storage_on_demand_card_view', input)
}

/** Fires `storage_on_demand_card_click` on tap/click of the search-injected
 *  cross-link card. Every click counts (no dedupe). NOT a conversion — see
 *  `onDemandCardView`. */
export const onDemandCardClick = (input: OnDemandEventInput = {}) =>
  fireOnDemandEvent('storage_on_demand_card_click', input)

// ---------------------------------------------------------------------------
// Stale-listing salvage — Meta out-of-stock signal
// ---------------------------------------------------------------------------
//
// When a user clicks a Meta DPA ad / cached short URL / stale share for a
// listing that's been deactivated, the listing route (or `/c/[nanoId]`)
// redirects to a recovery surface (pillar+city, hood, or search) with
// `?lost=1&lostId=<canonical-url>` query params.
//
// The destination page mounts <LostListingToast/>, which shows a toast AND
// calls this helper. We fire Meta `ViewContent` with `availability: 'out of
// stock'` and `content_ids: [<canonical-url>]` so Meta's "Items from Website"
// catalog refresher picks up the deactivation and stops serving the DPA
// creative on its next refresh cycle (~24h).
//
// Why URL as content_id: Meta's "Items from Website" catalog auto-keys items
// by URL when no `product:retailer_item_id` OG tag is present. Using the full
// canonical URL keeps the pixel signal aligned with whatever Meta crawled
// from the live listing page back when it was healthy. If we ever add a
// stable `retailer_item_id` (the UUID), this helper would shift to use that.

/**
 * Fires Meta `ViewContent` with `availability: 'out of stock'` so the catalog
 * stops serving DPA creatives for a deactivated listing. Idempotent — Meta
 * dedupes by event_id within ~5 min. Skips on internal traffic.
 *
 * @param contentId — Canonical listing URL (or other stable Meta catalog ID).
 */
export const markListingOutOfStock = (contentId: string) => {
  if (isInternalTraffic()) return
  if (!contentId) return

  const eventId = getEventId()

  pushToDataLayer('spotme_listing_out_of_stock', {
    event_id: eventId,
    content_id: contentId
  })

  fbp.event('ViewContent', {
    event_id: eventId,
    content_ids: [contentId],
    content_type: 'product',
    availability: 'out of stock',
    currency: 'MXN'
  })
}

// ---------------------------------------------------------------------------
// On-demand — pixel-as-product fanout (Meta DPA retargeting + GA4 view_item)
// ---------------------------------------------------------------------------
//
// Treats on-demand landing pages as virtual "products" in the Meta Catalog +
// GA4 + Bing UET sense, so we can run Advantage+ Catalog Ads / Dynamic Product
// Ads against visitors who viewed an on-demand surface but didn't submit.
//
// Stable content_ids (must match the Meta product feed CSV uploaded to
// Commerce Manager → Catalog → Storage):
//   - ondemand-parent     — /renta-mini-bodegas/almacenamiento-a-domicilio
//   - ondemand-monterrey  — .../almacenamiento-a-domicilio/monterrey
//   - ondemand-cdmx       — .../almacenamiento-a-domicilio/cdmx
//
// The `link` column in the feed points at the canonical landing URL — we do
// NOT mint fake /c/{nanoId} URLs (the backend would 404 on lookup and any
// cached short-URL share would break).
//
// Pricing anchors against the small-tier `indicativePricing` ($799 MXN) so
// the feed and the pixel agree. Currency is MXN throughout.

const ON_DEMAND_CATALOG_PRICE_MXN = 799

/** Build the stable Meta/GA4 catalog ID for an on-demand surface. */
const onDemandContentId = (city: string | undefined): string =>
  city ? `ondemand-${norm(city)}` : 'ondemand-parent'

/**
 * Fires the pixel-as-product fanout for an on-demand landing page mount.
 * Additive to `onDemandView` — keeps the GA4 `storage_on_demand_view` event
 * intact (campaign-friendly bare event) AND adds the catalog-shaped signal
 * (`view_item` + Meta `ViewContent` with `content_ids`) that DPA needs.
 *
 * Mirrors the listing-page `viewItem` fanout (fbq ViewContent + GA4 view_item
 * with `items` array + Bing UET view_item + dataLayer push). Marketing can
 * build a Meta Custom Audience on "ViewContent where content_ids contains
 * 'ondemand-'" for retargeting, and GA4 cohorts on `view_item` with
 * `item_category='storage-ondemand'` for funnel slicing.
 */
export const viewOnDemandContent = ({ city }: OnDemandEventInput = {}) => {
  if (isInternalTraffic()) return
  const eventId = getEventId()
  const contentId = onDemandContentId(city)
  const contentName = city
    ? `Almacenamiento a Domicilio — ${city}`
    : 'Almacenamiento a Domicilio'
  const price = ON_DEMAND_CATALOG_PRICE_MXN

  const payload = {
    event_id: eventId,
    vertical: 'storage' as const,
    pillar: 'storage-ondemand' as const,
    content_id: contentId,
    price,
    currency: 'MXN' as const,
    ...(city ? { city: norm(city) } : {})
  }

  pushToDataLayer('spotme_view_item_ondemand', payload)

  ga.event('view_item', {
    ...payload,
    items: [
      {
        item_id: contentId,
        item_name: contentName,
        item_category: 'storage-ondemand',
        price,
        quantity: 1
      }
    ]
  })

  fbp.event('ViewContent', {
    event_id: eventId,
    content_ids: [contentId],
    content_type: 'product',
    content_category: 'storage-ondemand',
    content_name: contentName,
    contents: [{ id: contentId, quantity: 1 }],
    currency: 'MXN',
    value: price
  })

  ms.event('view_item', {
    category: 'storage-ondemand',
    label: contentName,
    value: price.toString()
  })
}

// ---------------------------------------------------------------------------
// Supply (host) — funnel events
// ---------------------------------------------------------------------------
//
// Supply-side conversion events for /anfitrion/* pages. Strictly separate
// from the demand-side `generate_lead` / `Lead` / `parking_b2b_*` /
// `traditional_lead` / `storage_qualified_lead` family.
//
// Why separate: ad platforms optimize on event NAME, not on parameters.
// Reusing demand event names for supply-side conversions trains paid
// campaigns on opposite-intent users — a `parking_b2b_qualified_lead`
// campaign would optimize for a 50/50 mix of property owners (supply) and
// fleet renters (demand). Solution: a dedicated `supply_*` namespace.
//
// Each `supplyLead()` call fires:
//   GA4         → `supply_lead`  +  `supply_lead_${funnel}`
//   Meta Pixel  → custom `SupplyLead` (NOT standard `Lead` — keeps demand clean)
//   Bing UET    → `supply_lead`  +  `supply_lead_${funnel}`
//   LinkedIn    → `supply_lead` conversion (id from LINKEDIN_CONVERSION_IDS)
//   GTM         → `spotme_supply_lead`
//   Internal    → `SUPPLY_LEAD` event row via trackEvent (server-side report)
//
// Demand pages MUST NOT call any supply_* helper. Supply pages MUST NOT
// call `generateLead` or anything that fires the demand events above.

/**
 * Supply funnel buckets. Each /anfitrion/* surface maps to exactly one.
 * Marketing wires Google Ads / Meta / Bing campaigns to either the bare
 * `supply_lead` event (any host signup) or to a per-funnel variant
 * (`supply_lead_business`, `supply_lead_traditional`, etc.) depending on
 * how granular the campaign goal needs to be.
 */
export type SupplyFunnel =
  | 'individual' // /anfitrion (main host landing — individual property owners)
  | 'business' // /anfitrion/negocio (B2B host — corporate properties)
  | 'traditional' // /anfitrion/tradicional (Anexo C broker-mediated supply)
  | 'calculate' // /anfitrion/valua-tu-propiedad (calculator submit)
  | 'broker' // /anfitrion/broker (broker program signup)
  | 'logistics' // /anfitrion/soluciones-logisticas (3PL operator/host supply)
  | 'invite' // /anfitrion/invita (referral / invite a friend)
  | 'add_post' // AddPostFlow Success step — host actually published a listing

type SupplyEventInput = {
  funnel: SupplyFunnel
  /** Page path the event fired from — e.g. `/anfitrion/negocio`. */
  pagePath?: string
  /** Optional surface-specific label (e.g. `hero` vs `final_cta` vs `whatsapp`).
   *  Lets marketing slice within a funnel without splitting the event name. */
  source?: string
  /** Optional locationType from the calculator submit (`/valua-tu-propiedad`). */
  locationType?: LocationType
}

/** Per-funnel GA4/UET/LinkedIn event name. Only `supply_lead` fans out
 *  per-funnel — `supply_lead_start` and `supply_view` only fire the bare
 *  bucket event because per-funnel mid-funnel signals aren't worth the
 *  taxonomy noise. The literal strings here are members of both the GA4
 *  `Gtag` union and the Bing UET `Action` union (kept in sync). */
type SupplyLeadFunnelEvent =
  | 'supply_lead_individual'
  | 'supply_lead_business'
  | 'supply_lead_traditional'
  | 'supply_lead_calculate'
  | 'supply_lead_broker'
  | 'supply_lead_logistics'
  | 'supply_lead_invite'
  | 'supply_lead_add_post'

const supplyLeadFunnelEvent = (funnel: SupplyFunnel): SupplyLeadFunnelEvent =>
  `supply_lead_${funnel}` as SupplyLeadFunnelEvent

/**
 * Meta-only "first real answer" on the host calculator (the space type was
 * picked). `supply_lead_start` fires on the first pointer-down anywhere on the
 * page, which is too shallow to optimise a paid campaign on; the Meta supply
 * test (docs/superpowers/specs/2026-10-14-meta-supply-test-mty-design.md §4)
 * optimises on this instead via the custom conversion "Supply · inicio".
 * Pixel only: GA4/PostHog already carry `calc_type_select`.
 */
export const supplyCalcAnswer = (): void => {
  if (isInternalTraffic()) return
  fbp.trackCustom('SupplyLead', {
    event_id: getEventId(),
    type: 'supply_calc_answer',
    funnel: 'calculate'
  })
}

const fireSupplyEvent = (
  baseName:
    | 'supply_view'
    | 'supply_lead_start'
    | 'supply_lead'
    | 'supply_listing_posted',
  { funnel, pagePath, source, locationType }: SupplyEventInput
) => {
  if (isInternalTraffic()) return
  const eventId = getEventId()
  const payload: Record<string, string | number> = {
    event_id: eventId,
    funnel,
    lead_side: 'supply',
    ...(pagePath ? { page_path: pagePath } : {}),
    ...(source ? { source } : {}),
    ...(locationType ? { locationType } : {})
  }

  // GTM dataLayer
  pushToDataLayer(`spotme_${baseName}`, payload)

  // GA4 — bucket event + per-funnel event ONLY for the conversion-tier
  // events (`supply_lead` and `supply_listing_posted`). `supply_view` and
  // `supply_lead_start` stay bucket-only because per-funnel mid-funnel
  // signals aren't worth the taxonomy noise.
  ga.event(baseName, payload)
  if (baseName === 'supply_lead' || baseName === 'supply_listing_posted') {
    ga.event(supplyLeadFunnelEvent(funnel), payload)
  }

  // Meta Pixel — custom event with funnel param. Deliberately NOT the
  // standard `Lead` event — keeps `Lead` 100% demand-side.
  fbp.trackCustom('SupplyLead', {
    event_id: eventId,
    // The FULL event name: supply_view | supply_lead_start | supply_lead |
    // supply_listing_posted. Meta custom conversions match on this literal.
    type: baseName,
    funnel,
    ...(pagePath ? { page_path: pagePath } : {})
  })

  // Bing UET — bucket event + per-funnel for the conversion-tier events.
  ms.event(baseName, {
    category: 'supply',
    label: funnel,
    value: undefined
  })
  if (baseName === 'supply_lead' || baseName === 'supply_listing_posted') {
    ms.event(supplyLeadFunnelEvent(funnel), {
      category: 'supply',
      label: funnel
    })
  }

  // LinkedIn — bucket event always, per-funnel only for conversion-tier.
  // All conversions are no-ops until LINKEDIN_CONVERSION_IDS is populated.
  linkedin.trackConversion(baseName)
  if (baseName === 'supply_lead' || baseName === 'supply_listing_posted') {
    linkedin.trackConversion(supplyLeadFunnelEvent(funnel))
  }

  // Tier rollup — supply-side mirror of the demand-side rollup in
  // fireSegmentedEvent / fireOnDemandEvent. Same TIER_ROLLUP_MAP, same
  // recursion-safety (rollup events `host_primary` / `host_secondary` are
  // not themselves in the map). Rollup fires ONLY to GA4 + GTM dataLayer
  // — Meta / Bing / LinkedIn keep their existing funnel-specific events
  // (Meta's `SupplyLead` custom event already carries the funnel param).
  //
  // Per-funnel `supply_lead_${funnel}` variants are deliberately NOT in
  // the rollup map — they fire alongside the bare `supply_lead`, so
  // including them would double-count `host_primary`.
  //
  // Funnel-aware override: `supply_lead` with funnel=`individual` is a
  // pre-auth CTA click ("Subir tu espacio" buttons on /anfitrion via
  // useHandleSubmitLocation) — no contact info captured. Demote to
  // `host_secondary` so Smart Bidding doesn't treat a button-tap as a
  // captured lead. The 5 form-submit funnels (business, traditional,
  // calculate, broker, invite) keep `host_primary` — they push contact
  // data to HubSpot via sendCustomerInfoEmail.
  let rollup: Gtag | undefined = TIER_ROLLUP_MAP[baseName]
  if (baseName === 'supply_lead' && funnel === 'individual') {
    rollup = 'host_secondary'
  }
  if (rollup) {
    pushToDataLayer(`spotme_${rollup}`, payload)
    ga.event(rollup, payload)
  }
}

/**
 * Fires once per session on landing on any /anfitrion/* page. Pair with
 * the existing server-side `LANDING_VIEW` (pillar=host) — that one feeds
 * the marketing report; this one feeds paid pixel campaigns.
 *
 * For the server-side `SUPPLY_LEAD_START` / `SUPPLY_LEAD` rows that the
 * marketing report relies on, the calling component must ALSO call
 * `trackEvent` from `useTrackEvent` — same pattern as `addToCart`. The
 * helpers below stay pure (no Apollo / React coupling) so they can be
 * called from anywhere without circular imports.
 */
export const supplyView = (input: SupplyEventInput) =>
  fireSupplyEvent('supply_view', input)

/**
 * Fires on first form-field interaction on a supply lead form (typically
 * `onFocus` of the first field). Mid-funnel signal for measuring drop-off
 * between form view and form submit.
 */
export const supplyLeadStart = (input: SupplyEventInput) =>
  fireSupplyEvent('supply_lead_start', input)

/**
 * Fires on supply form submit (funnel=business/traditional/calculate/broker/
 * invite — captures contact info) OR supply CTA click (funnel=individual —
 * "Subir tu espacio" buttons, pre-auth, no contact captured).
 *
 * Tier behavior is funnel-aware (see fireSupplyEvent rollup override):
 *   - Form-submit funnels (5) → `host_primary` (they push to HubSpot)
 *   - CTA-click funnel (`individual`) → `host_secondary` (intent only)
 *
 * Marketing optimizes paid campaigns on `host_primary` (Google Ads
 * campaign goal) — so the demotion is what keeps Smart Bidding from
 * treating a button-tap as a captured lead.
 */
export const supplyLead = (input: SupplyEventInput) =>
  fireSupplyEvent('supply_lead', input)

/**
 * Fires on AddPostFlow Success step — the deepest supply-side signal,
 * meaning a host actually published a listing (not just submitted a contact
 * form). Analogous to `storage_purchase` / `parking_monthly_purchase` on
 * the demand side. Callers MUST gate firing once per listing so back-nav /
 * page reload on the Success page don't re-emit; today the call site in
 * AddPostFlow uses a ref-guard on `data.location.id`.
 *
 * Carries `funnel: 'add_post'` and rolls up into `host_primary` via
 * TIER_ROLLUP_MAP. Per-funnel variant `supply_lead_add_post` fires
 * alongside for fine-grained reporting.
 */
export const supplyListingPosted = (
  input: Omit<SupplyEventInput, 'funnel'> = {}
) => fireSupplyEvent('supply_listing_posted', { ...input, funnel: 'add_post' })
